import React from 'react';

import { useAppStore, usePlanningState } from '../store/hooks';
import { NarrativeState, SegmentId } from '../types/narrative';
import { getSiblingNodes, getSiblingNodesInSegment } from '../utils/narrativeHelpers';

import { ConceptPreview } from './ConceptPreview';
import { NodeCard } from './narrative-tree/NodeCard';
import { useArrows } from './narrative-tree/useArrows';

interface TreeViewProps {
  narrativeState: NarrativeState;
  currentNodeId: string;
  segmentId: SegmentId;
  onNodeClick: (nodeId: string) => void;
}

interface TreeLevel {
  nodes: string[];
  yPosition: number;
}

export const NarrativeTreeView = React.memo(
  function NarrativeTreeView({
    narrativeState,
    currentNodeId,
    segmentId,
    onNodeClick,
  }: TreeViewProps) {
    const containerRef = React.useRef<HTMLDivElement>(null);
    const nodeElementRefs = React.useRef<Record<string, HTMLDivElement | null>>({});
    const [dimensions, setDimensions] = React.useState({ width: 0, height: 0 });
    const [domNodePositions, setDomNodePositions] = React.useState(
      new Map<string, { x: number; y: number; width: number; height: number }>()
    );
    const planningState = usePlanningState();

    // Commented out verbose logging - uncomment for debugging
    // console.log('\n================== NarrativeTreeView Render ==================');
    // console.log('🌳 Rendering tree for segment:', segmentId);
    // console.log('📍 Current node ID:', currentNodeId);
    // console.log('📊 Total nodes in state:', Object.keys(narrativeState.nodes).length);
    // console.log('🕐 Render time:', new Date().toISOString());

    // Build levels for the tree - memoized to prevent recalculation
    const { levels } = React.useMemo(() => {
      const levels: TreeLevel[] = [];
      const processedNodes = new Set<string>();
      const nodePositions = new Map<string, { level: number; index: number }>();

      // Find root nodes in this segment, filtering for world variants if needed
      const segmentNodes = Object.values(narrativeState.nodes).filter((n) => {
        if (segmentId === '3A') {
          // Show only good world variant nodes
          return (
            n.id.startsWith('S3A_') ||
            (n.segmentId === segmentId && !n.id.startsWith('S3B_') && !n.id.startsWith('S3A_'))
          );
        } else if (segmentId === '3B') {
          // Show only bad world variant nodes
          return (
            n.id.startsWith('S3B_') ||
            (n.segmentId === segmentId && !n.id.startsWith('S3A_') && !n.id.startsWith('S3B_'))
          );
        } else {
          // Standard segment filtering
          return n.segmentId === segmentId;
        }
      });
      // console.log('📦 Nodes in segment', segmentId + ':', segmentNodes.map(n => n.id));

      const rootNodes = segmentNodes.filter(
        (node) =>
          !node.parentIds ||
          node.parentIds.length === 0 ||
          !node.parentIds.some((pid) => narrativeState.nodes[pid]?.segmentId === segmentId)
      );
      // console.log('🌱 Root nodes found:', rootNodes.map(n => n.id));

      // Build tree levels using BFS
      let currentLevel = rootNodes.map((n) => n.id);
      let levelIndex = 0;

      while (currentLevel.length > 0) {
        // console.log(`📏 Level ${levelIndex}:`, currentLevel);
        levels.push({ nodes: currentLevel, yPosition: levelIndex * 80 });

        // Mark nodes as processed and store positions
        currentLevel.forEach((nodeId, index) => {
          processedNodes.add(nodeId);
          nodePositions.set(nodeId, { level: levelIndex, index });
          // console.log(`  └─ Node ${nodeId} at position [${levelIndex}, ${index}]`);
        });

        // Get next level - all unique children
        const nextLevel = new Set<string>();
        currentLevel.forEach((nodeId) => {
          const node = narrativeState.nodes[nodeId];
          if (node.childIds && node.childIds.length > 0) {
            // console.log(`  🔗 Node ${nodeId} has children:`, node.childIds);
          }
          node.childIds?.forEach((childId) => {
            // Check if child should be included in this segment view
            const childNode = narrativeState.nodes[childId];
            let shouldIncludeChild = false;

            if (childNode && !processedNodes.has(childId)) {
              if (segmentId === '3A') {
                shouldIncludeChild =
                  childNode.id.startsWith('S3A_') ||
                  (childNode.segmentId === segmentId &&
                    !childNode.id.startsWith('S3A_') &&
                    !childNode.id.startsWith('S3B_'));
              } else if (segmentId === '3B') {
                shouldIncludeChild =
                  childNode.id.startsWith('S3B_') ||
                  (childNode.segmentId === segmentId &&
                    !childNode.id.startsWith('S3A_') &&
                    !childNode.id.startsWith('S3B_'));
              } else {
                shouldIncludeChild = childNode.segmentId === segmentId;
              }
            }

            if (shouldIncludeChild) {
              nextLevel.add(childId);
              // console.log(`    └─ Adding child ${childId} to next level`);
            } else if (processedNodes.has(childId)) {
              // console.log(`    └─ Skipping ${childId} (already processed)`);
            } else if (narrativeState.nodes[childId]?.segmentId !== segmentId) {
              // console.log(`    └─ Skipping ${childId} (different segment: ${narrativeState.nodes[childId]?.segmentId})`);
            }
          });
        });

        currentLevel = Array.from(nextLevel);
        levelIndex++;
      }

      return { levels };
    }, [narrativeState.nodes, segmentId]);

    // console.log('🎨 Final tree structure:');
    // console.log('  Levels:', levels.length);
    // levels.forEach((level, idx) => {
    //   console.log(`  Level ${idx}:`, level.nodes);
    // });
    // console.log('===============================================================\n');

    // Clear stored refs when levels change to avoid stale DOM references
    React.useEffect(() => {
      nodeElementRefs.current = {};
    }, [levels]);

    // Update dimensions when component mounts or updates
    React.useEffect(() => {
      if (containerRef.current) {
        let resizeTimeout: NodeJS.Timeout;

        const updateDimensions = () => {
          if (containerRef.current) {
            const { width, height } = containerRef.current.getBoundingClientRect();
            setDimensions({ width, height });
          }
        };

        // Initial update
        updateDimensions();

        // Debounced resize handler
        const handleResize = () => {
          clearTimeout(resizeTimeout);
          resizeTimeout = setTimeout(updateDimensions, 100);
        };

        window.addEventListener('resize', handleResize);
        return () => {
          window.removeEventListener('resize', handleResize);
          clearTimeout(resizeTimeout);
        };
      }
    }, [levels.length]);

    // Measure DOM positions of all nodes whenever layout changes - throttled
    React.useLayoutEffect(() => {
      if (!containerRef.current) return;

      // Use requestAnimationFrame to batch DOM measurements with throttling
      const measurePositions = () => {
        if (!containerRef.current) return;
        const containerRect = containerRef.current.getBoundingClientRect();
        const newPositions = new Map<
          string,
          { x: number; y: number; width: number; height: number }
        >();
        Object.entries(nodeElementRefs.current).forEach(([id, el]) => {
          if (el) {
            const rect = el.getBoundingClientRect();
            newPositions.set(id, {
              x: rect.left - containerRect.left,
              y: rect.top - containerRect.top,
              width: rect.width,
              height: rect.height,
            });
          }
        });
        setDomNodePositions(newPositions);
      };

      const rafId = requestAnimationFrame(measurePositions);
      return () => cancelAnimationFrame(rafId);
    }, [levels, dimensions, currentNodeId]);

    // Calculate node dimensions and spacing
    const NODE_WIDTH = 96; // w-24 = 96px
    const NODE_HEIGHT = 48; // h-12 = 48px
    const NODE_GAP = 8; // gap-2 = 8px
    const LEVEL_GAP = 20; // marginTop between levels
    const ACTIVE_NODE_WIDTH = 320; // Approximate width of active node
    const ACTIVE_NODE_HEIGHT = 200; // Approximate height of active node

    // Rough layout positions used before DOM is measured
    const nodeAbsolutePositions = React.useMemo(() => {
      const positions = new Map<string, { x: number; y: number; width: number; height: number }>();

      levels.forEach((level, levelIdx) => {
        const nodeCount = level.nodes.length;
        const levelWidth = nodeCount * NODE_WIDTH + (nodeCount - 1) * NODE_GAP;
        const startX = (dimensions.width - levelWidth) / 2;

        let yPosition = 0;
        for (let i = 0; i < levelIdx; i++) {
          const prevLevelHasActive = levels[i].nodes.some((nodeId) => nodeId === currentNodeId);
          yPosition += prevLevelHasActive ? ACTIVE_NODE_HEIGHT : NODE_HEIGHT;
          yPosition += LEVEL_GAP;
        }

        level.nodes.forEach((nodeId, nodeIdx) => {
          const isActive = nodeId === currentNodeId;
          const nodeWidth = isActive ? ACTIVE_NODE_WIDTH : NODE_WIDTH;
          const nodeHeight = isActive ? ACTIVE_NODE_HEIGHT : NODE_HEIGHT;

          let xPosition: number;
          if (isActive && nodeCount === 1) {
            xPosition = (dimensions.width - nodeWidth) / 2;
          } else if (isActive) {
            xPosition = startX + nodeIdx * (NODE_WIDTH + NODE_GAP) - (nodeWidth - NODE_WIDTH) / 2;
          } else {
            xPosition = startX + nodeIdx * (NODE_WIDTH + NODE_GAP);
          }

          positions.set(nodeId, {
            x: xPosition,
            y: yPosition,
            width: nodeWidth,
            height: nodeHeight,
          });
        });
      });

      return positions;
    }, [levels, dimensions.width, currentNodeId]);

    // Build array of all arrow connections
    const arrows = React.useMemo(() => {
      const arrows: Array<{ from: string; to: string }> = [];

      Object.values(narrativeState.nodes).forEach((node) => {
        // Check if node should be included in this segment view for arrows
        let shouldIncludeNode = false;
        if (segmentId === '3A') {
          shouldIncludeNode =
            node.id.startsWith('S3A_') ||
            (node.segmentId === segmentId &&
              !node.id.startsWith('S3A_') &&
              !node.id.startsWith('S3B_'));
        } else if (segmentId === '3B') {
          shouldIncludeNode =
            node.id.startsWith('S3B_') ||
            (node.segmentId === segmentId &&
              !node.id.startsWith('S3A_') &&
              !node.id.startsWith('S3B_'));
        } else {
          shouldIncludeNode = node.segmentId === segmentId;
        }

        if (shouldIncludeNode && node.childIds) {
          node.childIds.forEach((childId) => {
            const childNode = narrativeState.nodes[childId];
            let shouldIncludeChild = false;

            if (childNode) {
              if (segmentId === '3A') {
                shouldIncludeChild =
                  childNode.id.startsWith('S3A_') ||
                  (childNode.segmentId === segmentId &&
                    !childNode.id.startsWith('S3A_') &&
                    !childNode.id.startsWith('S3B_'));
              } else if (segmentId === '3B') {
                shouldIncludeChild =
                  childNode.id.startsWith('S3B_') ||
                  (childNode.segmentId === segmentId &&
                    !childNode.id.startsWith('S3A_') &&
                    !childNode.id.startsWith('S3B_'));
              } else {
                shouldIncludeChild = childNode.segmentId === segmentId;
              }
            }

            if (shouldIncludeChild) {
              // Special case: if this is a sibling node (shares parents with current node)
              // and it's not the active node, only show arrow if no sibling is active
              const currentNode = narrativeState.nodes[currentNodeId];
              const childNode = narrativeState.nodes[childId];

              if (
                currentNode &&
                childNode &&
                currentNode.parentIds &&
                node.parentIds &&
                currentNode.parentIds.some((pid) => node.parentIds?.includes(pid)) &&
                node.id !== currentNodeId
              ) {
                // This is a sibling of the current node
                // Check if any sibling in this level is active
                const siblingLevel = levels.find((level) => level.nodes.includes(node.id));
                const hasActiveSibling = siblingLevel?.nodes.some((nId) => nId === currentNodeId);

                // Only show arrow from active sibling when one is expanded
                if (hasActiveSibling) {
                  return; // Skip this arrow
                }
              }

              arrows.push({ from: node.id, to: childId });
            }
          });
        }
      });

      return arrows;
    }, [narrativeState.nodes, segmentId, currentNodeId, levels]);

    // console.log('🏹 Arrow connections:', arrows);
    // console.log('📏 Container dimensions:', dimensions);
    // console.log('📋 Node DOM positions:', Array.from(domNodePositions.entries()));

    // Calculate total height needed for the tree
    const totalHeight = React.useMemo(() => {
      const heightSource = domNodePositions.size > 0 ? domNodePositions : nodeAbsolutePositions;
      return heightSource.size > 0
        ? Math.max(...Array.from(heightSource.values()).map((pos) => pos.y + pos.height)) + 40
        : 400;
    }, [domNodePositions, nodeAbsolutePositions]);

    const arrowsSvg = useArrows(
      arrows,
      domNodePositions,
      nodeAbsolutePositions,
      dimensions,
      narrativeState,
      totalHeight
    );

    // Memoize click handlers for nodes
    const handleNodeClick = React.useCallback(
      (nodeId: string, isClickable: boolean) => {
        if (isClickable) {
          onNodeClick(nodeId);
        }
      },
      [onNodeClick]
    );

    // Helper function to check if a target node is accessible
    const isTargetNodeAccessible = React.useCallback(
      (targetNodeId: string) => {
        const targetNode = narrativeState.nodes[targetNodeId];
        if (!targetNode) return false;
        if (!targetNode.parentIds || targetNode.parentIds.length === 0) return true;

        const segment = targetNode.segmentId;

        return targetNode.parentIds.every((parentId) => {
          const parentNode = narrativeState.nodes[parentId];
          if (!parentNode) return false;

          if (parentNode.segmentId === segment) {
            if (parentNode.completionStatus !== 'complete') {
              return false;
            }

            if (parentNode.parentIds && parentNode.parentIds.length > 0) {
              const siblings = getSiblingNodesInSegment(narrativeState, parentId, segment);
              return siblings.every((sibling) => sibling.completionStatus === 'complete');
            }
          }

          return true;
        });
      },
      [narrativeState]
    );

    const handleChoiceClick = React.useCallback(
      (node: any, choiceIdx: number) => {
        const choice = node.choices?.[choiceIdx];
        if (!choice?.goto) return;

        const targetIsAccessible = isTargetNodeAccessible(choice.goto);

        if (!targetIsAccessible) {
          // Show error message for blocked navigation
          console.warn('Cannot navigate to next node: Complete all sibling nodes first');
          return;
        }

        // Check if this is a transition from segment 2 to segment 3
        if (node.id === 'S2_Return_To_A_Plot_Player_Led' && choice.goto?.includes('variant-')) {
          // Determine which world this choice leads to
          const worldChoice = choiceIdx === 0 || choiceIdx === 3 ? 'good' : 'bad';

          // Show the world state modal
          const { setShowWorldStateModal, setPendingWorldChoice, setClickedChoiceIndex } =
            useAppStore.getState();
          setClickedChoiceIndex(choiceIdx);
          setPendingWorldChoice(worldChoice);
          setShowWorldStateModal(true);
          return;
        }

        try {
          // Direct navigation for all other choices
          onNodeClick(choice.goto);
        } catch (error) {
          console.error('Error navigating to node:', error);
        }
      },
      [onNodeClick, isTargetNodeAccessible]
    );

    // Determine animation type based on current node state
    const animationType = React.useMemo(() => {
      const currentNode = narrativeState.nodes[currentNodeId];
      if (!currentNode || currentNode.completionStatus !== 'complete') return 'none';

      // Check if we're in a "Choose a path" scenario
      if (currentNode.choices && currentNode.choices.length > 0) {
        const hasAccessibleChild = currentNode.choices.some((choice) => {
          if (!choice.goto) return false;
          const targetNode = narrativeState.nodes[choice.goto];
          if (!targetNode || targetNode.segmentId !== segmentId) return false;
          if (!targetNode.parentIds || targetNode.parentIds.length === 0) return true;
          return targetNode.parentIds.every((parentId) => {
            const parentNode = narrativeState.nodes[parentId];
            return parentNode && parentNode.completionStatus === 'complete';
          });
        });

        if (hasAccessibleChild) {
          return 'choose-path';
        }
      }

      // Check if we're in a "Complete siblings" scenario
      const hasAccessibleChildren =
        currentNode.childIds?.some((childId) => {
          const child = narrativeState.nodes[childId];
          if (!child || child.segmentId !== segmentId) return false;

          return (
            child.parentIds?.every((parentId) => {
              const parent = narrativeState.nodes[parentId];
              return parent && parent.completionStatus === 'complete';
            }) ?? true
          );
        }) ?? false;

      if (!hasAccessibleChildren) {
        return 'complete-siblings';
      }

      return 'none';
    }, [narrativeState.nodes, currentNodeId, segmentId]);

    // Get nodes that should be animated based on animation type
    const animatedNodeIds = React.useMemo(() => {
      const animatedIds = new Set<string>();
      const currentNode = narrativeState.nodes[currentNodeId];
      if (!currentNode) return animatedIds;

      if (animationType === 'complete-siblings') {
        // Animate incomplete siblings
        const siblings = getSiblingNodes(narrativeState, currentNodeId);
        siblings.forEach((sibling) => {
          if (sibling.completionStatus !== 'complete' && sibling.segmentId === segmentId) {
            animatedIds.add(sibling.id);
          }
        });

        // Also animate incomplete nodes at previous levels
        const nodeLevel = levels.findIndex((level) => level.nodes.includes(currentNodeId));
        if (nodeLevel > 0) {
          for (let i = 0; i < nodeLevel; i++) {
            levels[i].nodes.forEach((nodeId) => {
              const node = narrativeState.nodes[nodeId];
              if (node && node.completionStatus !== 'complete') {
                animatedIds.add(nodeId);
              }
            });
          }
        }
      } else if (animationType === 'choose-path') {
        // Animate child nodes that choices lead to
        currentNode.choices?.forEach((choice) => {
          if (choice.goto) {
            const targetNode = narrativeState.nodes[choice.goto];
            if (targetNode && targetNode.segmentId === segmentId) {
              // Check if this child is accessible
              const isAccessible =
                !targetNode.parentIds ||
                targetNode.parentIds.length === 0 ||
                targetNode.parentIds.every((parentId) => {
                  const parent = narrativeState.nodes[parentId];
                  return parent && parent.completionStatus === 'complete';
                });

              if (isAccessible) {
                animatedIds.add(choice.goto);
              }
            }
          }
        });
      }

      return animatedIds;
    }, [animationType, narrativeState, currentNodeId, segmentId, levels]);

    return (
      <>
        <div className="w-full relative" ref={containerRef} style={{ minHeight: totalHeight }}>
          {/* Tip above preview */}
          <div className="mb-4 flex justify-center">
            <div className="bg-[#1a2d4d]/80 backdrop-blur-sm border border-[#4f46e5]/30 rounded-lg px-3 py-1.5 shadow-lg">
              <p className="text-xs text-gray-300">
                <span className="text-[#06b6d4] font-semibold">Tip:</span> Use this preview view to
                navigate and track progress
              </p>
            </div>
          </div>

          {/* Game Concept Card at the top */}
          <div className="mb-6">
            <ConceptPreview
              planningState={planningState}
              includeSegment2Summary={segmentId >= '2'}
            />
          </div>

          {/* Single SVG overlay for all arrows */}
          {arrowsSvg}

          {/* Render nodes */}
          <div className="relative" style={{ zIndex: 2 }}>
            {levels.map((level, levelIdx) => {
              return (
                <div
                  key={levelIdx}
                  className="flex justify-center"
                  style={{
                    marginTop: levelIdx === 0 ? 0 : LEVEL_GAP,
                    position: 'relative',
                  }}
                >
                  <div className="flex gap-2">
                    {level.nodes.map((nodeId) => {
                      const node = narrativeState.nodes[nodeId];
                      if (!node) {
                        console.error(`❌ Node ${nodeId} not found in narrativeState.nodes!`);
                      } else {
                        // console.log(`✅ Rendering node ${nodeId}:`, node.id, node.text?.substring(0, 50));
                      }
                      const isActive = nodeId === currentNodeId;

                      // Check if all parent nodes and their siblings are complete
                      const areParentsAndSiblingsComplete = () => {
                        if (!node.parentIds || node.parentIds.length === 0) return true;

                        const segment = node.segmentId;

                        return node.parentIds.every((parentId) => {
                          const parentNode = narrativeState.nodes[parentId];
                          if (!parentNode) return false;

                          if (parentNode.segmentId === segment) {
                            if (parentNode.completionStatus !== 'complete') {
                              return false;
                            }

                            if (parentNode.parentIds && parentNode.parentIds.length > 0) {
                              const siblings = getSiblingNodesInSegment(
                                narrativeState,
                                parentId,
                                segment
                              );
                              return siblings.every(
                                (sibling) => sibling.completionStatus === 'complete'
                              );
                            }
                          }

                          return true;
                        });
                      };

                      const isClickable = areParentsAndSiblingsComplete();

                      return (
                        <div
                          key={nodeId}
                          ref={(el) => {
                            nodeElementRefs.current[nodeId] = el;
                          }}
                        >
                          <NodeCard
                            node={node || { id: nodeId, text: '', choices: [] }}
                            narrativeState={narrativeState}
                            isActive={isActive}
                            isParent={false}
                            isMiniaturized={!isActive}
                            isClickable={isClickable}
                            shouldAnimate={animatedNodeIds.has(nodeId)}
                            onClick={() => handleNodeClick(nodeId, isClickable)}
                            onChoiceClick={(choiceIdx) => handleChoiceClick(node, choiceIdx)}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison - re-render only when key props change
    return (
      prevProps.currentNodeId === nextProps.currentNodeId &&
      prevProps.segmentId === nextProps.segmentId &&
      prevProps.onNodeClick === nextProps.onNodeClick &&
      // Deep compare relevant parts of narrativeState
      prevProps.narrativeState.currentSegmentId === nextProps.narrativeState.currentSegmentId &&
      prevProps.narrativeState.nodes === nextProps.narrativeState.nodes
    );
  }
);
